/* eslint-disable max-len */
import Fade from 'components/UI_kit_3/animations/Fade/Fade';
import React, {
  cloneElement, isValidElement, ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';

export type TriggerType = 'click' | 'hover';

export type Position = 'top' | 'top-left' | 'top-right' |
'bottom' | 'bottom-left' | 'bottom-right' |
'left' | 'left-top' | 'left-bottom' |
'right' | 'right-top' | 'right-bottom';

type RenderPosition = {
  top: number
  left: number,
  translateX: null | string,
  translateY: null | string,
  minWidth?: string
};

const initialPosition: RenderPosition = {
  top: 0,
  left: 0,
  translateX: null,
  translateY: null,
};

export type OverlayTriggerProps = {
  className?: string;
  trigger?: TriggerType;
  overlay: ReactElement;
  children: ReactElement;
  animationDuration?: number
  position?: Position
  offset?: number
  active?: boolean
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onExited?: () => void
};

const OverlayTrigger = ({
  trigger = 'click',
  animationDuration = 100,
  position = 'top',
  offset = 8,
  active = true,
  overlay,
  className,
  children,
  open: openProp,
  onOpenChange,
  onExited,
}: OverlayTriggerProps) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);

  const open = isControlled ? openProp : internalOpen;

  const setOpenState = (value: boolean) => {
    if (!isControlled) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };

  const [renderPosition, setRenderPosition] = useState<RenderPosition>(initialPosition);

  if (!isValidElement(children)) {
    throw new Error('OverlayTrigger expects single ReactElement as child');
  }

  const getTransformStyle = (pos: RenderPosition) => {
    if (pos.translateX && pos.translateY) {
      return `translate(${renderPosition.translateX}, ${renderPosition.translateY})`;
    }
    if (pos.translateX) {
      return `translateX(${renderPosition.translateX})`;
    }
    if (pos.translateY) {
      return `translateY(${renderPosition.translateY})`;
    }
    return undefined;
  };

  const updatePosition = (_position?: Position) => {
    if (!triggerRef.current || !overlayRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const popupRect = overlayRef.current.getBoundingClientRect();
    const currentPosition = _position || position;

    const edgeOffset = 12;

    let top = 0;
    let left = 0;

    // базовая позиция
    switch (currentPosition) {
      case 'top-left':
        top = rect.top + window.scrollY - offset - popupRect.height;
        left = rect.left + window.scrollX;
        break;
      case 'top-right':
        top = rect.top + window.scrollY - offset - popupRect.height;
        left = rect.right + window.scrollX - popupRect.width;
        break;
      case 'bottom':
        top = rect.bottom + window.scrollY + offset;
        left = rect.left + window.scrollX + rect.width / 2 - popupRect.width / 2;
        break;
      case 'bottom-left':
        top = rect.bottom + window.scrollY + offset;
        left = rect.left + window.scrollX;
        break;
      case 'bottom-right':
        top = rect.bottom + window.scrollY + offset;
        left = rect.right + window.scrollX - popupRect.width;
        break;
      case 'left':
        top = rect.top + window.scrollY + rect.height / 2 - popupRect.height / 2;
        left = rect.left + window.scrollX - offset - popupRect.width;
        break;
      case 'left-top':
        top = rect.top + window.scrollY;
        left = rect.left + window.scrollX - offset - popupRect.width;
        break;
      case 'left-bottom':
        top = rect.bottom + window.scrollY - popupRect.height;
        left = rect.left + window.scrollX - offset - popupRect.width;
        break;
      case 'right':
        top = rect.top + window.scrollY + rect.height / 2 - popupRect.height / 2;
        left = rect.right + window.scrollX + offset;
        break;
      case 'right-top':
        top = rect.top + window.scrollY;
        left = rect.right + window.scrollX + offset;
        break;
      case 'right-bottom':
        top = rect.bottom + window.scrollY - popupRect.height;
        left = rect.right + window.scrollX + offset;
        break;
      case 'top':
      default:
        top = rect.top + window.scrollY - offset - popupRect.height;
        left = rect.left + window.scrollX + rect.width / 2 - popupRect.width / 2;
        break;
    }

    // авто-флип по вертикали
    if (top < window.scrollY && currentPosition.startsWith('top')) {
      top = rect.bottom + window.scrollY + offset; // top → bottom
    } else if (top + popupRect.height > window.scrollY + window.innerHeight
    && currentPosition.startsWith('bottom')) {
      top = rect.top + window.scrollY - offset - popupRect.height; // bottom → top
    }

    // авто-флип по горизонтали
    if (left < window.scrollX && currentPosition.startsWith('left')) {
      left = rect.right + window.scrollX + offset; // left → right
    } else if (left + popupRect.width > window.scrollX + window.innerWidth
    && currentPosition.startsWith('right')) {
      left = rect.left + window.scrollX - offset - popupRect.width; // right → left
    }

    // сдвиг внутрь viewport по X
    if (left < window.scrollX) left = window.scrollX + edgeOffset;
    if (left + popupRect.width > window.scrollX + window.innerWidth) {
      left = window.scrollX + window.innerWidth - popupRect.width - edgeOffset;
    }

    // сдвиг внутрь viewport по Y
    if (top < window.scrollY) top = window.scrollY + edgeOffset;
    if (top + popupRect.height > window.scrollY + window.innerHeight) {
      top = window.scrollY + window.innerHeight - popupRect.height - edgeOffset;
    }

    setRenderPosition({
      top, left, translateX: null, translateY: null, minWidth: `${rect.width}px`,
    });
  };

  const handleScroll = () => {
    if (open) {
      updatePosition();
    }
  };

  const handleExited = () => {
    setRenderPosition(initialPosition);
    onExited?.();
  };

  // click outside
  useEffect(() => {
    if (!open || trigger !== 'click') return () => {};

    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;

      // Проверяем, не находится ли клик внутри элемента с data-overlay-ignore
      const isIgnoredElement = target instanceof Element
    && target.closest('[data-overlay-ignore="true"]');

      if (
        isIgnoredElement
    || overlayRef.current?.contains(target)
    || triggerRef.current?.contains(target)
      ) {
        return;
      }
      setOpenState(false);
    };

    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, trigger]);

  useLayoutEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open, trigger]);

  useLayoutEffect(() => {
    if (openProp) {
      updatePosition();
    }
  }, [openProp]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  const triggerProps = useMemo(() => {
    if (openProp !== undefined) {
      return {};
    }
    if (trigger === 'click') {
      return {
        onClick: (e:MouseEvent) => {
          if (!active) return;
          updatePosition();
          setOpenState(!open);
          e.stopPropagation();
        },
      };
    }
    if (trigger === 'hover') {
      return {
        onMouseEnter: () => {
          if (!active) return;
          updatePosition();
          setOpenState(true);
        },
        onMouseLeave: () => {
          setOpenState(false);
        },
      };
    }
    return {};
  }, [open, trigger, active]);

  return (
    <>
      {cloneElement(children, {
        ...triggerProps,
        ref: triggerRef,
      } as any)}
      {createPortal(
        <Fade
          ref={overlayRef}
          isVisible={open}
          duration={animationDuration}
          mountOnEnter
          unmountOnExit
          onEnter={() => updatePosition()}
          onExited={handleExited}
        >
          <div
            className={className}
            data-overlay-ignore
            ref={(ref) => { overlayRef.current = ref; }}
            style={{
              position: 'absolute',
              top: renderPosition.top,
              left: renderPosition.left,
              transform: getTransformStyle(renderPosition),
              minWidth: renderPosition.minWidth,
              zIndex: 1000,
              // иначе оверлей перекрывает соседние триггеры и крадет у них mouseenter
              pointerEvents: trigger === 'hover' ? 'none' : undefined,
            }}
          >
            {overlay}
          </div>
        </Fade>,
        document.body,
      )}
    </>
  );
};

export default OverlayTrigger;
